import React, { useState, useEffect } from 'react';
import { AspectRatio, Box, Button, Stack, Typography, Input, IconButton } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { MediaPicker } from '../MediaPicker';
import { Trash as DeleteIcon } from '@phosphor-icons/react';

interface SelectedImage {
  url: string;
  file?: File;
  alt?: string;
  productionPhotoId?: number;
}

interface MarketingPhotosStepProps {
  siteId: number | null;
  images: SelectedImage[];
  onChange: (images: SelectedImage[]) => void;
}

export const MarketingPhotosStep: React.FC<MarketingPhotosStepProps> = ({
  siteId,
  images,
  onChange,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const autoImportMutation = useMutation({
    mutationFn: async () => {
      if (!siteId) return [];
      const response = await api.post('/api/cms/media/import-latest/', { site: siteId });
      return (response as any).imported || [];
    },
    onSuccess: (imported: any[]) => {
      const mapped = imported.map((m: any) => ({ url: m.file_url, alt: m.alt_text }));
      if (mapped.length) {
        onChange([...images, ...mapped]);
      }
    }
  });

  useEffect(() => {
    if (siteId && images.length === 0 && autoImportMutation.isIdle) {
      autoImportMutation.mutate();
    }
  }, [siteId, images.length]);

  const handleSelect = (media: { url: string; alt?: string }) => {
    // Check if this image URL already exists to avoid duplicates
    const exists = images.some(img => img.url === media.url);
    if (!exists) {
      onChange([...images, media]);
    }
    // Don't close the picker here, let MediaPicker handle it
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newImages = files.map((file) => ({
      url: URL.createObjectURL(file),
      file,
    }));
    onChange([...images, ...newImages]);
  };

  return (
    <Box>
      <Typography level="h4" mb={2}>
        Add Photos of Your Work
      </Typography>
      <Stack direction="row" spacing={2} mb={2}>
        <Button onClick={() => setPickerOpen(true)}>
          Choose from Gallery
        </Button>
        <Button component="label" variant="outlined" color="primary">
          Upload New
          <input type="file" multiple hidden onChange={handleUpload} />
        </Button>
        <Button
          variant="soft"
          onClick={() => autoImportMutation.mutate()}
          disabled={!siteId}
          loading={autoImportMutation.isPending}
        >
          Auto-import Recent Marketing Photos
        </Button>
      </Stack>
      <MediaPicker
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      onSelect={handleSelect}
      siteId={siteId || 0}
      />
      <Box display="flex" flexWrap="wrap" gap={2}>
        {images.map((img, idx) => (
          <Box key={idx} sx={{ position: 'relative' }}>
            <AspectRatio ratio="1" sx={{ width: 80 }}>
              <img src={img.url} alt={img.alt} />
            </AspectRatio>
            <IconButton
              size="sm"
              color="danger"
              sx={{ position: 'absolute', top: 0, right: 0, transform: 'translate(50%, -50%)' }}
              onClick={() => {
                const newImages = images.filter((_, i) => i !== idx);
                onChange(newImages);
              }}
            >
              <DeleteIcon />
            </IconButton>
            <Input
              size="sm"
              placeholder="Image description"
              sx={{ mt: 1 }}
              value={img.alt || ''}
              onChange={(e) => {
                const newImages = [...images];
                newImages[idx] = { ...newImages[idx], alt: e.target.value };
                onChange(newImages);
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MarketingPhotosStep;
